// src/ui/theme/pageAnatomy.js
// MANIFIESTO DE ANATOMIA del editor de estilos. Describe cada ambito (General o
// una pagina) como una lista ORDENADA de bloques = los componentes reales que
// construyen esa pantalla. El editor lee esto para montar, en el mismo orden:
//   - el acordeon de edicion (centro), bloque a bloque, y
//   - la vista previa descompuesta (derecha), un espejo por bloque.
//
// Asi el diseñador edita "lo que ve": en Salas solo aparece lo de Salas.
// Escalar a otra pagina = anadir una entrada aqui, sin tocar el motor.
//
// Forma de un bloque:
//   {
//     key:     id unico del bloque (tambien marca el espejo a iluminar)
//     label:   titulo de la seccion en el acordeon
//     desc?:   frase de contexto bajo el titulo
//     preview: id del espejo a renderizar (ver StylePreviews.js)
//     controls: [ control ]
//   }
//
// Forma de un control (3 tipos):
//   { kind:"token", token:"color.primary", label?, hint? }  // reusa token de tema
//   { kind:"image", slot:"salas.guide.step1", label, hint } // imagen por pagina
//   { kind:"text",  slot:"salas.guide.step1.title", label, hint, type?:"input"|"textarea" }
//
// label/hint de un control "token" caen al TOKEN_CATALOG si no se especifican;
// cuando se especifican, CONTEXTUALIZAN el token al componente (el mismo
// color.primary se llama "Anillo activo" en los tiles y "Boton de la card" en la
// card). Eso ayuda al diseñador a entender el efecto en cada sitio.

export const SCOPE_BASE = "base";

// ----------------------------------------------------------------------------
// GENERAL (base): solo lo transversal a TODAS las paginas. Sin imagenes de
// contenido (esas van por pagina); aqui solo el logo del marco.
// ----------------------------------------------------------------------------
const BASE_BLOCKS = [
  {
    key: "layoutIzq",
    label: "Layout izquierdo (menu lateral)",
    desc: "La barra de navegacion de la izquierda, comun a toda la plataforma.",
    preview: "sidebar",
    controls: [
      { kind: "token", token: "color.sidebarBg" },
      { kind: "token", token: "sidebar.itemText" },
      { kind: "token", token: "sidebar.itemActiveBg" },
      { kind: "token", token: "sidebar.itemActiveText" },
      { kind: "token", token: "sidebar.itemHoverBg" },
      { kind: "token", token: "sidebar.itemRadius" },
      {
        kind: "image",
        slot: "logo.full",
        label: "Logo completo",
        hint: "Logo de la cabecera del menu (vacio = el actual).",
      },
      {
        kind: "image",
        slot: "logo.icon",
        label: "Logo icono",
        hint: "Version reducida del logo (vacio = el actual).",
      },
    ],
  },
  {
    key: "menuNames",
    label: "Nombres del menu",
    desc: "Renombra las paginas del menu lateral y elige la tipografia de esos nombres. El nombre que pongas aqui es el mismo que veras en el selector de paginas del editor.",
    preview: "sidebar",
    controls: [
      {
        kind: "text",
        slot: "nav.scenes",
        label: "Nombre · Escenas",
        hint: "Nombre de la pagina de catalogo (hoy 'Escenas').",
        type: "input",
      },
      {
        kind: "text",
        slot: "nav.aiAssistants",
        label: "Nombre · Asistentes IA",
        type: "input",
      },
      {
        kind: "text",
        slot: "nav.evaluations",
        label: "Nombre · Evaluaciones",
        type: "input",
      },
      {
        kind: "text",
        slot: "nav.itineraries",
        label: "Nombre · Itinerarios",
        type: "input",
      },
      {
        kind: "text",
        slot: "nav.tasks",
        label: "Nombre · Tareas",
        type: "input",
      },
      {
        kind: "text",
        slot: "nav.educamaker",
        label: "Nombre · Educamaker",
        type: "input",
      },
      {
        kind: "typography",
        role: "sidebar",
        label: "Tipografia del menu",
        hint: "Fuente, peso (negrita) y cursiva de los nombres del menu.",
        noScale: true,
        tokens: {
          family: "sidebar.itemFont",
          weight: "sidebar.itemWeight",
          style: "sidebar.itemStyle",
        },
      },
    ],
  },
  {
    key: "layoutSup",
    label: "Layout superior (cabecera)",
    desc: "La franja superior comun (fondo, bordes y acento).",
    preview: "topbar",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo de la cabecera" },
      { kind: "token", token: "color.border", label: "Borde inferior" },
      {
        kind: "token",
        token: "color.primary",
        label: "Acento de la cabecera",
        hint: "Color de iconos/acciones de la franja superior.",
      },
    ],
  },
  {
    key: "typography",
    label: "Tipografia",
    desc: "Fuente, tamano, peso (negrita) y cursiva de los titulos y del texto, comun a toda la plataforma.",
    preview: "typography",
    controls: [
      {
        kind: "typography",
        role: "display",
        label: "Titulos",
        hint: "Encabezados de pantallas y cards.",
      },
      {
        kind: "typography",
        role: "body",
        label: "Texto",
        hint: "Texto general, descripciones y botones.",
      },
    ],
  },
  {
    key: "guiaColors",
    label: "Guia rapida (colores)",
    desc: "Colores del bloque 'Guia rapida' que aparece en cada pagina. Las imagenes y textos de los pasos se editan dentro de cada pagina.",
    preview: "guide",
    guidePrefix: "salas.guide",
    controls: [
      {
        kind: "token",
        token: "color.tintSoft",
        label: "Fondo de la guia",
        hint: "Tinte del bloque 'Guia rapida' (cabecera y pasos).",
      },
      {
        kind: "token",
        token: "color.primary",
        label: "Acento de la guia",
        hint: "Icono de la bombilla, numeros de paso y 'Ver guia'.",
      },
      { kind: "token", token: "color.text", label: "Titulo de los pasos" },
      {
        kind: "token",
        token: "color.textMuted",
        label: "Descripcion de los pasos",
      },
      { kind: "token", token: "radius.card", label: "Redondez del bloque" },
    ],
  },
  {
    key: "confirmDialog",
    label: "Dialogo de confirmacion",
    desc: "El dialogo que aparece al cerrar un formulario o modal ('¿Estas seguro...?'). Es comun a toda la plataforma.",
    preview: "confirm",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del dialogo" },
      { kind: "token", token: "color.text", label: "Texto del dialogo" },
      { kind: "token", token: "color.border", label: "Bordes y separador" },
      {
        kind: "token",
        token: "color.confirmAccent",
        label: "Boton de confirmar (OK)",
        hint: "Color del boton de confirmacion (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez del dialogo" },
    ],
    note: "Hoy este dialogo sale con el tema legacy (azul) porque se renderiza FUERA del tema de marca (queda huerfano). El espejo muestra el diseño correcto (verde); el dev lo tematiza a marca al materializar.",
  },
];

// ----------------------------------------------------------------------------
// SALAS (teacher/salas): la anatomia real, en orden de arriba a abajo.
// ----------------------------------------------------------------------------
// Controles de una guia rapida de N pasos (por defecto 3) para un prefijo dado
// (salas.guide, assistant.guide, ...). Reutilizable en cualquier pagina.
function guideControls(prefix, count = 3) {
  return [
    {
      kind: "text",
      slot: `${prefix}.title`,
      label: "Titulo de la guia",
      hint: "Encabezado del bloque (vacio = 'Guia rapida').",
      type: "input",
    },
    ...Array.from({ length: count }, (_, i) => i + 1).flatMap((n) => [
      {
        kind: "image",
        slot: `${prefix}.step${n}`,
        label: `Paso ${n} · ilustracion`,
        hint: `Imagen del paso ${n} de la guia (vacio = la actual).`,
      },
      {
        kind: "text",
        slot: `${prefix}.step${n}.title`,
        label: `Paso ${n} · titulo`,
        hint: `Titulo del paso ${n} (vacio = texto por defecto).`,
        type: "input",
      },
      {
        kind: "text",
        slot: `${prefix}.step${n}.desc`,
        label: `Paso ${n} · descripcion`,
        hint: `Descripcion del paso ${n} (vacio = texto por defecto).`,
        type: "textarea",
      },
    ]),
  ];
}

const salasGuideControls = guideControls("salas.guide");

const SALAS_BLOCKS = [
  {
    key: "salasTexts",
    label: "Textos de la pagina",
    desc: "El titulo y la descripcion grandes de la cabecera de Salas.",
    preview: "pageTexts",
    controls: [
      {
        kind: "text",
        slot: "pageTitle",
        label: "Titulo de la pagina",
        hint: "Encabezado grande (vacio = texto por defecto).",
        type: "input",
      },
      {
        kind: "text",
        slot: "pageDescription",
        label: "Descripcion",
        hint: "Texto bajo el titulo (vacio = texto por defecto).",
        type: "textarea",
      },
    ],
  },
  {
    key: "salasGuide",
    label: "Guia rapida (imagenes y textos)",
    desc: "Las ilustraciones y textos de los 3 pasos de la guia de Salas. Los colores son comunes (se editan en General).",
    preview: "guide",
    guidePrefix: "salas.guide",
    controls: salasGuideControls,
  },
  {
    key: "salasTiles",
    label: "Categorias (tiles)",
    desc: "Los tiles de categoria con icono y etiqueta. El color del punto de cada categoria es programatico (fijo); aqui se cambian los iconos y la forma del tile.",
    preview: "tiles",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del tile" },
      { kind: "token", token: "color.border", label: "Borde del tile" },
      {
        kind: "token",
        token: "color.tileRing",
        label: "Anillo del tile activo",
        hint: "Color del marco/anillo del tile seleccionado (independiente del primario).",
      },
      { kind: "token", token: "color.text", label: "Texto del tile" },
      { kind: "token", token: "radius.card", label: "Redondez del tile" },
      {
        kind: "image",
        slot: "salas.cat.humanas",
        label: "Icono · Ciencias Humanas",
        hint: "Icono del tile de Ciencias Humanas.",
      },
      {
        kind: "image",
        slot: "salas.cat.naturales",
        label: "Icono · Ciencias Naturales",
        hint: "Icono del tile de Ciencias Naturales.",
      },
      {
        kind: "image",
        slot: "salas.cat.historia",
        label: "Icono · Historia",
        hint: "Icono del tile de Historia.",
      },
      {
        kind: "image",
        slot: "salas.cat.matematicas",
        label: "Icono · Matematicas",
        hint: "Icono del tile de Matematicas.",
      },
      {
        kind: "image",
        slot: "salas.cat.arte",
        label: "Icono · Arte",
        hint: "Icono del tile de Arte.",
      },
      {
        kind: "image",
        slot: "salas.cat.vida",
        label: "Icono · Vida Saludable",
        hint: "Icono del tile de Vida Saludable.",
      },
    ],
  },
  {
    key: "salasChips",
    label: "Subcategorias (chips)",
    desc: "Los chips de subcategoria (Prehistoria, Edad Antigua, ...).",
    preview: "chips",
    controls: [
      {
        kind: "token",
        token: "color.chipActive",
        label: "Chip activo",
        hint: "Fondo/borde del chip de subcategoria seleccionado (independiente del primario).",
      },
      { kind: "token", token: "color.textMuted", label: "Texto del chip" },
      { kind: "token", token: "color.border", label: "Borde del chip" },
    ],
  },
  {
    key: "salasCard",
    label: "Card de sala",
    desc: "La tarjeta de cada escena: miniatura, titulo, categoria y botones.",
    preview: "sceneCard",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo de la card" },
      { kind: "token", token: "color.border", label: "Borde de la card" },
      { kind: "token", token: "color.text", label: "Titulo de la card" },
      {
        kind: "token",
        token: "color.textMuted",
        label: "Texto secundario",
        hint: "'Creado por', subcategoria.",
      },
      {
        kind: "token",
        token: "color.cardAccent",
        label: "Boton 'Anadir a itinerario'",
        hint: "Color del boton principal de la card (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez de la card" },
      { kind: "token", token: "font.display", label: "Fuente del titulo" },
    ],
  },
  {
    key: "salasItinerary",
    label: "Mis Itinerarios (card)",
    desc: "La tarjeta de itinerario de la columna derecha.",
    preview: "itinerary",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo de la card" },
      { kind: "token", token: "color.border", label: "Borde de la card" },
      { kind: "token", token: "color.text", label: "Nombre del itinerario" },
      {
        kind: "token",
        token: "color.itinAccent",
        label: "Etiqueta e iconos",
        hint: "Tag de privacidad y acciones (ver/editar) (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez de la card" },
    ],
  },
  {
    key: "salasModal",
    label: "Modal crear/editar itinerario",
    desc: "El dialogo de pasos para crear o editar un itinerario.",
    preview: "modal",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del modal" },
      { kind: "token", token: "color.text", label: "Texto del modal" },
      {
        kind: "token",
        token: "color.modalAccent",
        label: "Boton 'Siguiente' y pasos",
        hint: "Color del boton primario y del paso activo (independiente del primario).",
      },
      { kind: "token", token: "color.border", label: "Bordes de inputs" },
      { kind: "token", token: "radius.card", label: "Redondez del modal" },
    ],
  },
];

// ----------------------------------------------------------------------------
// ASISTENTES IA (teacher/assistant)
// ----------------------------------------------------------------------------
const ASSISTANT_BLOCKS = [
  {
    key: "asisTexts",
    label: "Textos de la pagina",
    desc: "El titulo y la descripcion de la cabecera de Asistentes IA.",
    preview: "pageTexts",
    controls: [
      {
        kind: "text",
        slot: "pageTitle",
        label: "Titulo de la pagina",
        hint: "Encabezado grande (vacio = texto por defecto).",
        type: "input",
      },
      {
        kind: "text",
        slot: "pageDescription",
        label: "Descripcion",
        hint: "Texto bajo el titulo (vacio = texto por defecto).",
        type: "textarea",
      },
    ],
  },
  {
    key: "asisGuide",
    label: "Guia rapida (imagenes y textos)",
    desc: "Las ilustraciones y textos de los 3 pasos de la guia de Asistentes. Los colores son comunes (se editan en General).",
    preview: "guide",
    guidePrefix: "assistant.guide",
    controls: guideControls("assistant.guide"),
  },
  {
    key: "asisTabs",
    label: "Pestanas (Educa360 / Mis asistentes)",
    desc: "El selector de pestanas entre los asistentes de Educa360 y los tuyos.",
    preview: "tabs",
    controls: [
      {
        kind: "token",
        token: "color.tabActive",
        label: "Pestana activa",
        hint: "Fondo de la pestana seleccionada (independiente del primario).",
      },
      { kind: "token", token: "color.surface", label: "Fondo del selector" },
      { kind: "token", token: "color.text", label: "Texto de las pestanas" },
      {
        kind: "token",
        token: "radius.control",
        label: "Redondez del selector",
      },
    ],
  },
  {
    key: "asisCard",
    label: "Card de asistente",
    desc: "La tarjeta de cada asistente: avatar, nombre, etiqueta y boton Chatear.",
    preview: "assistantCard",
    controls: [
      {
        kind: "choice",
        token: "radius.avatarShape",
        label: "Forma del avatar",
        hint: "Forma de la imagen del asistente.",
        options: [
          { value: "50%", label: "Circulo" },
          { value: "16px", label: "Redondeado" },
          { value: "6px", label: "Poco redondeado" },
          { value: "0px", label: "Cuadrado" },
        ],
      },
      {
        kind: "number",
        token: "radius.avatarSize",
        label: "Tamano del avatar",
        hint: "Diametro de la imagen del asistente (px).",
        min: 40,
        max: 96,
        unit: "px",
      },
      { kind: "token", token: "color.surface", label: "Fondo de la card" },
      { kind: "token", token: "color.border", label: "Borde de la card" },
      { kind: "token", token: "color.text", label: "Nombre del asistente" },
      {
        kind: "token",
        token: "color.cardAccent",
        label: "Etiqueta y boton 'Chatear'",
        hint: "Color de la etiqueta de autor y del boton principal (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez de la card" },
      { kind: "token", token: "font.display", label: "Fuente del nombre" },
    ],
  },
  {
    key: "asisModal",
    label: "Modal crear asistente",
    desc: "El dialogo para crear o editar un asistente IA.",
    preview: "modal",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del modal" },
      { kind: "token", token: "color.text", label: "Texto del modal" },
      {
        kind: "token",
        token: "color.modalAccent",
        label: "Boton primario",
        hint: "Color del boton de accion del modal (independiente del primario).",
      },
      { kind: "token", token: "color.border", label: "Bordes de inputs" },
      { kind: "token", token: "radius.card", label: "Redondez del modal" },
    ],
  },
];

// ----------------------------------------------------------------------------
// EVALUACIONES (teacher/quiz)
// ----------------------------------------------------------------------------
const QUIZ_BLOCKS = [
  {
    key: "quizTexts",
    label: "Textos de la pagina",
    desc: "El titulo y la descripcion de la cabecera de Evaluaciones.",
    preview: "pageTexts",
    controls: [
      {
        kind: "text",
        slot: "pageTitle",
        label: "Titulo de la pagina",
        hint: "Encabezado grande (vacio = texto por defecto).",
        type: "input",
      },
      {
        kind: "text",
        slot: "pageDescription",
        label: "Descripcion",
        hint: "Texto bajo el titulo (vacio = texto por defecto).",
        type: "textarea",
      },
    ],
  },
  {
    key: "quizGuide",
    label: "Guia rapida (imagenes y textos)",
    desc: "Las ilustraciones y textos de los 3 pasos de la guia de Evaluaciones. Los colores son comunes (se editan en General).",
    preview: "guide",
    guidePrefix: "quiz.guide",
    controls: guideControls("quiz.guide"),
  },
  {
    key: "quizTable",
    label: "Tabla de evaluaciones",
    desc: "El listado de evaluaciones: cabecera, filas, contador de preguntas y acciones.",
    preview: "table",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo de la tabla" },
      { kind: "token", token: "color.border", label: "Lineas de la tabla" },
      {
        kind: "token",
        token: "color.text",
        label: "Cabecera y titulos de fila",
      },
      {
        kind: "token",
        token: "color.textMuted",
        label: "Descripcion de cada fila",
      },
      {
        kind: "token",
        token: "color.tableAccent",
        label: "Contador e iconos de accion",
        hint: "Badge de '# preguntas' e iconos editar/ver (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez de la tabla" },
    ],
  },
  {
    key: "quizModal",
    label: "Modal crear evaluacion",
    desc: "El dialogo con nombre, descripcion y el constructor de preguntas.",
    preview: "quizModal",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del modal" },
      { kind: "token", token: "color.text", label: "Texto del modal" },
      { kind: "token", token: "color.border", label: "Bordes de inputs" },
      {
        kind: "token",
        token: "color.modalAccent",
        label: "Progreso, opcion correcta y boton",
        hint: "Barra de progreso, radio de respuesta correcta y boton 'Crear evaluacion' (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez del modal" },
    ],
  },
];

// ----------------------------------------------------------------------------
// ITINERARIOS (teacher/evaluacion)
// ----------------------------------------------------------------------------
const ITINERARIO_BLOCKS = [
  {
    key: "evalTexts",
    label: "Textos de la pagina",
    desc: "El titulo y la descripcion de la cabecera de Itinerarios.",
    preview: "pageTexts",
    controls: [
      {
        kind: "text",
        slot: "pageTitle",
        label: "Titulo de la pagina",
        hint: "Encabezado grande (vacio = texto por defecto).",
        type: "input",
      },
      {
        kind: "text",
        slot: "pageDescription",
        label: "Descripcion",
        hint: "Texto bajo el titulo (vacio = texto por defecto).",
        type: "textarea",
      },
    ],
  },
  {
    key: "evalGuide",
    label: "Guia rapida (imagenes y textos)",
    desc: "Las ilustraciones y textos de los 3 pasos de la guia de Itinerarios. Los colores son comunes (se editan en General).",
    preview: "guide",
    guidePrefix: "evaluacion.guide",
    controls: guideControls("evaluacion.guide"),
  },
  {
    key: "routeCard",
    label: "Card de itinerario",
    desc: "La tarjeta de cada itinerario: portada, etiqueta, nombre, clase y boton Entrar.",
    preview: "routeCard",
    controls: [
      {
        kind: "image",
        slot: "itinerario.cover",
        label: "Portada del itinerario",
        hint: "Imagen de portada (es generica para todos; vacio = la actual).",
      },
      { kind: "token", token: "color.surface", label: "Fondo de la card" },
      { kind: "token", token: "color.border", label: "Borde de la card" },
      { kind: "token", token: "color.text", label: "Nombre del itinerario" },
      {
        kind: "token",
        token: "color.textMuted",
        label: "Texto 'Asignado a'",
      },
      {
        kind: "token",
        token: "color.cardAccent",
        label: "Boton 'Entrar'",
        hint: "Color del boton principal (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez de la card" },
    ],
  },
  {
    key: "evalScenes",
    label: "Escenas del itinerario",
    desc: "Las tarjetas de escena del itinerario seleccionado (miniatura, titulo, categoria y boton Entrar).",
    preview: "sceneCard",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo de la card" },
      { kind: "token", token: "color.border", label: "Borde de la card" },
      { kind: "token", token: "color.text", label: "Titulo de la escena" },
      {
        kind: "token",
        token: "color.textMuted",
        label: "Texto secundario",
        hint: "'Creado por', subcategoria.",
      },
      {
        kind: "token",
        token: "color.cardAccent",
        label: "Boton 'Entrar'",
        hint: "Color del boton principal (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez de la card" },
      { kind: "token", token: "font.display", label: "Fuente del titulo" },
    ],
  },
  {
    key: "classPanel",
    label: "Panel de clase",
    desc: "El panel derecho 'Clase': asistentes asignados y estudiantes del itinerario.",
    preview: "classPanel",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del panel" },
      { kind: "token", token: "color.border", label: "Separadores" },
      {
        kind: "token",
        token: "color.text",
        label: "Titulo y nombres",
        hint: "'Clase: 1A' y nombres de asistentes/estudiantes.",
      },
      {
        kind: "token",
        token: "color.textMuted",
        label: "Secciones y subtexto",
        hint: "'Asistentes IA asignados', 'Asistente Virtual', clase.",
      },
      {
        kind: "token",
        token: "color.panelBar",
        label: "Barra de seccion",
        hint: "La barrita vertical junto a cada titulo de seccion (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez del panel" },
    ],
    note: "El punto verde de estado (online) es programatico (no editable), igual que el color de categoria.",
  },
  {
    key: "evalTestModal",
    label: "Modal seleccionar test",
    desc: "El dialogo 'Selecciona un Test para esta escena' con las opciones y botones.",
    preview: "testModal",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del modal" },
      { kind: "token", token: "color.text", label: "Texto del modal" },
      { kind: "token", token: "color.border", label: "Bordes y radios" },
      {
        kind: "token",
        token: "color.modalAccent",
        label: "Opcion marcada y boton",
        hint: "Radio de la opcion elegida y boton 'Asignar y Entrar' (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez del modal" },
    ],
  },
];

// ----------------------------------------------------------------------------
// TAREAS (teacher/tareas)
// ----------------------------------------------------------------------------
const TAREAS_BLOCKS = [
  {
    key: "tareasTexts",
    label: "Textos de la pagina",
    desc: "El titulo y la descripcion de la cabecera de Tareas.",
    preview: "pageTexts",
    controls: [
      {
        kind: "text",
        slot: "pageTitle",
        label: "Titulo de la pagina",
        hint: "Encabezado grande (vacio = texto por defecto).",
        type: "input",
      },
      {
        kind: "text",
        slot: "pageDescription",
        label: "Descripcion",
        hint: "Texto bajo el titulo (vacio = texto por defecto).",
        type: "textarea",
      },
    ],
  },
  {
    key: "tareasGuide",
    label: "Guia rapida (imagenes y textos)",
    desc: "Las ilustraciones y textos de los 5 pasos de la guia de Tareas. Los colores son comunes (se editan en General).",
    preview: "guide",
    guidePrefix: "tareas.guide",
    guideSteps: 5,
    controls: guideControls("tareas.guide", 5),
  },
  {
    key: "tareasTable",
    label: "Tabla 'Mis tareas'",
    desc: "El listado de tareas: cabecera, filas, clase, numero de pasos, estado y acciones.",
    preview: "tasksTable",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo de la tabla" },
      { kind: "token", token: "color.border", label: "Lineas de la tabla" },
      {
        kind: "token",
        token: "color.text",
        label: "Cabecera y titulos de fila",
      },
      {
        kind: "token",
        token: "color.textMuted",
        label: "Clase y numero de pasos",
      },
      {
        kind: "token",
        token: "color.tableAccent",
        label: "Iconos de accion",
        hint: "Iconos editar/pausar de cada fila (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez de la tabla" },
    ],
    note: "La etiqueta de estado (Activa/Pausada/Borrador) usa colores semanticos por estado (no editable), igual que el punto de estado.",
  },
  {
    key: "tareasWizard",
    label: "Asistente de creacion (wizard)",
    desc: "El dialogo de 4 pasos para crear una tarea: pasos, tarjetas de seleccion, flujo de la leccion, cuestionario y resumen.",
    preview: "wizard",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del asistente" },
      { kind: "token", token: "color.text", label: "Titulos y textos" },
      {
        kind: "token",
        token: "color.textMuted",
        label: "Subtextos y etiquetas",
      },
      {
        kind: "token",
        token: "color.border",
        label: "Bordes de inputs y tarjetas",
      },
      {
        kind: "token",
        token: "color.wizardAccent",
        label: "Pasos, botones, seleccion y toggle",
        hint: "Paso activo, botones (Siguiente/Activar/Seleccionar), tarjeta elegida, interruptor y numero de orden (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez del asistente" },
    ],
  },
];

// ----------------------------------------------------------------------------
// EDUCAMAKER (teacher/educamaker) — pagina "Mis escenas"
// ----------------------------------------------------------------------------
const EDUCAMAKER_BLOCKS = [
  {
    key: "eduTexts",
    label: "Textos de la pagina",
    desc: "El titulo y la descripcion de la cabecera de 'Mis escenas'.",
    preview: "pageTexts",
    controls: [
      {
        kind: "text",
        slot: "pageTitle",
        label: "Titulo de la pagina",
        hint: "Encabezado grande (vacio = texto por defecto).",
        type: "input",
      },
      {
        kind: "text",
        slot: "pageDescription",
        label: "Descripcion",
        hint: "Texto bajo el titulo (vacio = texto por defecto).",
        type: "textarea",
      },
    ],
  },
  {
    key: "eduGuide",
    label: "Guia rapida (imagenes y textos)",
    desc: "Las ilustraciones y textos de los 3 pasos de la guia de Educamaker. Los colores son comunes (se editan en General).",
    preview: "guide",
    guidePrefix: "vscenes.guide",
    controls: guideControls("vscenes.guide", 3),
  },
  {
    key: "eduCard",
    label: "Card de escena creada",
    desc: "La tarjeta de cada escena creada: miniatura, titulo, categoria y botones Ver/Editar/Borrar.",
    preview: "eduCard",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo de la card" },
      { kind: "token", token: "color.border", label: "Borde de la card" },
      { kind: "token", token: "color.text", label: "Titulo de la escena" },
      { kind: "token", token: "color.textMuted", label: "Subcategoria" },
      {
        kind: "token",
        token: "color.cardAccent",
        label: "Botones 'Ver' y 'Editar'",
        hint: "Color del boton Ver y del borde de Editar (independiente del primario). 'Borrar' es semantico (rojo).",
      },
      { kind: "token", token: "radius.card", label: "Redondez de la card" },
      { kind: "token", token: "font.display", label: "Fuente del titulo" },
    ],
  },
  {
    key: "eduModal",
    label: "Modal editar escena",
    desc: "El dialogo de 2 pasos (Modulo 00 / Modulo 01) para editar una escena externa.",
    preview: "modal",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo del modal" },
      { kind: "token", token: "color.text", label: "Texto del modal" },
      { kind: "token", token: "color.border", label: "Bordes de inputs" },
      {
        kind: "token",
        token: "color.modalAccent",
        label: "Paso activo y boton 'Siguiente'",
        hint: "Color del paso activo y del boton principal (independiente del primario).",
      },
      { kind: "token", token: "radius.card", label: "Redondez del modal" },
    ],
  },
];

// ----------------------------------------------------------------------------
// MARCA (brand): pestana global con aviso. Solo el color primario; escribe en
// la capa base, asi que cascada a TODA la plataforma.
// ----------------------------------------------------------------------------
const BRAND_BLOCKS = [
  {
    key: "brandColor",
    label: "Color primario de la marca",
    desc: "El color principal de Educa360. Es el interruptor maestro: botones, acentos y todos los componentes que lo heredan cambian con el.",
    preview: "brand",
    controls: [
      {
        kind: "token",
        token: "color.primary",
        label: "Color primario",
        hint: "Color de marca. Afecta a toda la plataforma a la vez.",
      },
    ],
    note: "AVISO: cambiar este color afecta a TODAS las paginas a la vez. Solo se mantienen los elementos a los que hayas puesto un color propio en una pagina concreta (ese override gana).",
  },
];

export const PAGE_ANATOMY = {
  brand: BRAND_BLOCKS,
  [SCOPE_BASE]: BASE_BLOCKS,
  "teacher/salas": SALAS_BLOCKS,
  "teacher/assistant": ASSISTANT_BLOCKS,
  "teacher/quiz": QUIZ_BLOCKS,
  "teacher/evaluacion": ITINERARIO_BLOCKS,
  "teacher/tareas": TAREAS_BLOCKS,
  "teacher/educamaker": EDUCAMAKER_BLOCKS,
};

// Bloques del ambito pedido; si una pagina aun no tiene anatomia propia,
// devolvemos un bloque generico para no dejar el editor vacio.
export function getAnatomy(scope) {
  return PAGE_ANATOMY[scope] || GENERIC_PAGE_BLOCKS;
}

// Fallback para paginas todavia no descompuestas (se iran anadiendo).
const GENERIC_PAGE_BLOCKS = [
  {
    key: "genericTexts",
    label: "Textos de la pagina",
    desc: "Titulo y descripcion de la cabecera.",
    preview: "pageTexts",
    controls: [
      {
        kind: "text",
        slot: "pageTitle",
        label: "Titulo de la pagina",
        hint: "Encabezado grande (vacio = texto por defecto).",
        type: "input",
      },
      {
        kind: "text",
        slot: "pageDescription",
        label: "Descripcion",
        hint: "Texto bajo el titulo (vacio = texto por defecto).",
        type: "textarea",
      },
    ],
  },
  {
    key: "genericCard",
    label: "Tarjetas y botones",
    desc: "Aspecto generico de tarjetas, textos y botones de la pagina.",
    preview: "sceneCard",
    controls: [
      { kind: "token", token: "color.surface", label: "Fondo de tarjeta" },
      { kind: "token", token: "color.border", label: "Bordes" },
      { kind: "token", token: "color.text", label: "Titulos" },
      { kind: "token", token: "color.textMuted", label: "Texto secundario" },
      { kind: "token", token: "color.primary", label: "Botones y acentos" },
      { kind: "token", token: "radius.card", label: "Redondez de tarjetas" },
    ],
  },
];

export default PAGE_ANATOMY;
